import { WebLayout } from 'components/Layout';
import { motion } from 'framer-motion';
import {
  Smartphone,
  CreditCard,
  Database,
  Settings,
  Users,
  Cloud,
  Lock,
  TrendingUp,
  Zap,
  Briefcase,
  Globe,
} from 'react-feather';

// Animation Variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const hoverCard = { whileHover: { scale: 1.05, boxShadow: '0 0 30px rgba(247,147,30,0.4)' } };

// Section Heading with subtle accent line
const SectionHeading = ({ children }) => (
  <motion.h2
    className="relative text-4xl sm:text-5xl font-extrabold text-white tracking-tight"
    initial="hidden"
    whileInView="visible"
    variants={fadeUp}
  >
    {children}
    <span className="block mt-2 h-[3px] w-24 bg-gradient-to-r from-[#F7931E] to-transparent"></span>
  </motion.h2>
);

export function ProductsServices() {
  return (
    <WebLayout>
      {/* Hero Section */}
      <section className="relative z-10 text-center py-28 ">
        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-5xl sm:text-6xl font-extrabold text-white"
        >
          Products & Services
        </motion.h1>
        <p className="mt-6 text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
          Powering Africa's oil & gas logistics sector with{' '}
          <strong>smart finance, real-time payments, and scalable digital tools</strong>.
        </p>
      </section>

      {/* CATEGORY 1: User-Facing Platforms */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-20">
        <SectionHeading>User-Facing Platforms</SectionHeading>
        <p className="mt-4 text-gray-400 max-w-4xl">
          Tools for riders, transporters, contractors, and fuel providers — enabling fuel payments,
          credit, and operational efficiency.
        </p>

        <motion.div
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
        >
          {[
            {
              icon: <Smartphone className="text-[#F7931E]" size={40} />,
              title: 'LonaPay Mobile App',
              desc: 'Android/iOS app for riders & fleet operators. Request Gas Tokens, repay, track balances, and find nearby partner stations (Google Maps integrated).',
            },
            {
              icon: <CreditCard className="text-[#F7931E]" size={40} />,
              title: 'Gas Token Wallet',
              desc: 'Daily or 48-hour fuel tokens, approved by providers, redeemable at partner stations. Late fee tracking, repayments via Mobile Money or Bank.',
            },
            {
              icon: <Users className="text-[#F7931E]" size={40} />,
              title: 'AdvancePay for Contractors',
              desc: 'Upload invoices and get 50–70% of funds upfront. Auto-settlement with buyers, full audit logs, and easy repayment tracking.',
            },
            {
              icon: <Cloud className="text-[#F7931E]" size={40} />,
              title: 'Provider Portal',
              desc: 'Fuel stations, banks, and lenders can manage profiles, apply commissions, monitor balances, and approve rider/token requests.',
            },
            {
              icon: <Zap className="text-[#F7931E]" size={40} />,
              title: 'USSD Interface',
              desc: 'For rural or offline areas — riders and operators can check balances, request tokens, and repay via a simple phone menu.',
            },
            {
              icon: <TrendingUp className="text-[#F7931E]" size={40} />,
              title: 'Investor Access',
              desc: 'Micro-investors and institutions can buy into fuel-backed assets, track returns, and monitor tokenized reserves (launching 2026).',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              {...hoverCard}
              className="p-6 bg-[#111]/60 rounded-xl border border-[#F7931E]/20 shadow-md text-gray-300"
            >
              {item.icon}
              <h3 className="mt-4 text-xl font-bold text-white">{item.title}</h3>
              <p className="mt-3 text-sm text-gray-400">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CATEGORY 2: Enterprise & Admin Tools */}
      <section className="bg-[#0e0e0e] py-20 px-6 sm:px-12">
        <SectionHeading>Enterprise & Admin Tools</SectionHeading>
        <p className="mt-4 text-gray-400 max-w-4xl">
          Powerful back-office and enterprise systems for HQ teams, auditors, and admins to manage
          LonaPay’s network.
        </p>

        <motion.div
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
        >
          {[
            {
              icon: <Settings className="text-[#F7931E]" size={40} />,
              title: 'HQ Admin Console',
              desc: 'Roles, permissions, fee configuration, FX rates, branding, and notification settings. Includes activity audit logs and admin approval flow.',
            },
            {
              icon: <Database className="text-[#F7931E]" size={40} />,
              title: 'FuelChain Transaction Engine',
              desc: 'Automates settlements, multi-party splits (Depot, Tax, Station), and exports for finance/auditors. Trigger-based payment releases.',
            },
            {
              icon: <Lock className="text-[#F7931E]" size={40} />,
              title: 'Advanced Security Module',
              desc: 'Mandatory 2FA for admins, IP whitelisting, session device tracking, and secure logs. Every critical action is recorded.',
            },
            {
              icon: <TrendingUp className="text-[#F7931E]" size={40} />,
              title: 'Real-Time Dashboards',
              desc: 'Role-specific, customizable dashboards for Admin, Provider, and Investors — drag-and-drop KPIs, charts, and exports (CSV, PDF).',
            },
            {
              icon: <Users className="text-[#F7931E]" size={40} />,
              title: 'Provider Management Suite',
              desc: 'Approve/suspend providers, assign transaction caps, set commissions, and monitor Gas Token issuance and repayments.',
            },
            {
              icon: <Briefcase className="text-[#F7931E]" size={40} />,
              title: 'Contractor Finance Tools',
              desc: 'Track AdvancePay disbursements, invoices, settlements, and reconciliation, all linked to the FuelChain ledger.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              {...hoverCard}
              className="p-6 bg-[#111]/60 rounded-xl border border-[#F7931E]/20 shadow-md text-gray-300"
            >
              {item.icon}
              <h3 className="mt-4 text-xl font-bold text-white">{item.title}</h3>
              <p className="mt-3 text-sm text-gray-400">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CATEGORY 3: Integrations & APIs */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-20">
        <SectionHeading>APIs & Integrations</SectionHeading>
        <p className="mt-4 text-gray-400 max-w-4xl">
          LonaPay is API-first, designed for seamless integration with payment gateways, partners,
          and analytics platforms.
        </p>

        <motion.div
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
        >
          {[
            {
              icon: <Cloud className="text-[#F7931E]" size={40} />,
              title: 'Payment Gateway APIs',
              desc: 'Flutterwave (mobile money, bank), Paystack (card), MTN Mobile Money API for direct transactions.',
            },
            {
              icon: <Globe className="text-[#F7931E]" size={40} />,
              title: 'Maps & Location Services',
              desc: 'Google Maps API integration for fuel station discovery, route planning, and GPS-based payment confirmations.',
            },
            {
              icon: <Lock className="text-[#F7931E]" size={40} />,
              title: 'Security & Notifications',
              desc: 'Twilio/SMS for alerts, encrypted webhooks for partners, and secure OAuth2/JWT authentication for all apps.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              {...hoverCard}
              className="p-6 bg-[#111]/60 rounded-xl border border-[#F7931E]/20 shadow-md text-gray-300"
            >
              {item.icon}
              <h3 className="mt-4 text-xl font-bold text-white">{item.title}</h3>
              <p className="mt-3 text-sm text-gray-400">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CATEGORY 4: Future Innovations */}
      <section className="bg-[#0e0e0e] py-20 px-6 sm:px-12">
        <SectionHeading>Future Innovations</SectionHeading>
        <p className="mt-4 text-gray-400 max-w-4xl">
          Our roadmap includes next-gen tools to revolutionize fuel finance in East Africa.
        </p>

        <motion.div
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
        >
          {[
            {
              icon: <Zap className="text-[#F7931E]" size={40} />,
              title: 'Blockchain Fuel Tokens',
              desc: 'Exploring Stellar or Hyperledger for verifiable, tokenized Gas Tokens, improving liquidity and security.',
            },
            {
              icon: <TrendingUp className="text-[#F7931E]" size={40} />,
              title: 'AI Route Optimization',
              desc: 'AI-driven prediction for fuel stops and delivery optimization for fleet operators and drivers.',
            },
            {
              icon: <Users className="text-[#F7931E]" size={40} />,
              title: 'Micro-Investment Marketplace',
              desc: 'Allowing retail and institutional investors to buy fractional shares in LonaPay-backed reserves.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              {...hoverCard}
              className="p-6 bg-[#111]/60 rounded-xl border border-[#F7931E]/20 shadow-md text-gray-300"
            >
              {item.icon}
              <h3 className="mt-4 text-xl font-bold text-white">{item.title}</h3>
              <p className="mt-3 text-sm text-gray-400">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-24 text-center">
        <motion.h3
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-4xl sm:text-5xl font-bold text-white"
        >
          Power Your Energy Finance with LonaPay
        </motion.h3>
        <p className="mt-4 text-gray-400 max-w-3xl mx-auto">
          Whether you’re a rider, provider, contractor, or investor, LonaPay’s ecosystem is built to
          fuel your growth. Join our pilot program today.
        </p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          className="mt-8 px-8 py-4 bg-[#F7931E] text-black font-bold rounded-lg shadow-lg hover:bg-[#FFA94D] transition"
        >
          Get Started
        </motion.button>
      </section>
    </WebLayout>
  );
}
